import { useCallback, useEffect, useRef, useState } from 'react';

import { checkForToken, fetchOauthURL } from '../../../../../../core/api/external-sheets';

export interface GoogleSheetsTokenResult {
  hasToken: boolean;
  authUrl?: string;
  error?: string;
}

/**
 * Hook that will check the master google sheet token status for the current user
 * @param redirectUrl - In the case of needing to sign into google sheets (the URL for that will be
 *                      returned by this hook if required), this is the URL the user would be redirected
 *                      back to afterwards
 */
export const useGoogleSheetsToken = (redirectUrl: string) => {
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<GoogleSheetsTokenResult>();
  const currentRequest = useRef<number>();

  const requestTokenInfo = useCallback(async () => {
    try {
      const result = await checkForToken();
      return {
        hasToken: result.token_already_exists,
        error: undefined
      };
    } catch (e) {
      return {
        hasToken: false,
        error: 'Failed to check export authorisation status'
      };
    }
  }, []);

  const requestOauthUrl = useCallback(async () => {
    try {
      const result = await fetchOauthURL(redirectUrl);
      return {
        error: result.error || undefined,
        url: result.url || undefined
      };
    } catch (e) {
      return {
        error: 'Failed to authorise import sheet creation. Please contact support.',
        url: undefined
      };
    }
  }, [redirectUrl]);

  const refresh = useCallback(async () => {
    const request = (currentRequest.current = Date.now());
    setIsLoading(true);

    const tokenData = await requestTokenInfo();

    const needOauthUrl = !tokenData.hasToken && !tokenData.error;
    const oAuthUrlData = needOauthUrl ? await requestOauthUrl() : undefined;

    if (request === currentRequest.current) {
      setResult({
        hasToken: tokenData.hasToken,
        authUrl: oAuthUrlData?.url,
        error: tokenData.error || oAuthUrlData?.error
      });
      setIsLoading(false);
    }
  }, [requestOauthUrl, requestTokenInfo]);

  // Load data on first run
  useEffect(() => {
    refresh();
  }, [refresh]);

  return { isLoading, refresh, result };
};
